import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';

// ---- Helpers ----
const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const statusBadge = s => s === 'Active' ? 'badge-green' : s === 'Inactive' ? 'badge-amber' : 'badge-red';
const typeBadge = t => t === 'Intern' ? 'badge-amber' : 'badge-indigo';

// ---- Promote Modal ----
function PromoteModal({ empId, designations, employees, onClose, onDone }) {
  const [form, setForm] = useState({ designation_id:'', employment_type:'Permanent', start_date:'', salary:'', reporting_to:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/employees/${empId}/promote`, form);
      onDone();
    } catch (e) { alert(e.response?.data?.message || 'Error'); setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">Promote / Change Designation</span>
          <button className="btn btn-icon btn-outline" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{background:'var(--amber-pale)',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#92400e',marginBottom:16}}>
            ⚡ Previous designation history will be fully preserved with dates and duration.
          </div>
          <div className="form-grid">
            <div className="form-group"><label>New Designation *</label>
              <select value={form.designation_id} onChange={e=>set('designation_id',e.target.value)}>
                <option value="">Select</option>
                {Object.entries(designations.reduce((a,d)=>({...a,[d.department]:[...(a[d.department]||[]),d]}),{})).map(([dept,list]) => (
                  <optgroup key={dept} label={dept}>{list.map(d=><option key={d.id} value={d.id}>{d.title}</option>)}</optgroup>
                ))}
              </select>
            </div>
            <div className="form-group"><label>Employment Type *</label>
              <select value={form.employment_type} onChange={e=>set('employment_type',e.target.value)}>
                <option>Intern</option><option>Permanent</option><option>Contract</option><option>Part-Time</option>
              </select>
            </div>
            <div className="form-group"><label>Effective Date *</label><input type="date" value={form.start_date} onChange={e=>set('start_date',e.target.value)} /></div>
            <div className="form-group"><label>New Salary (LKR)</label><input type="number" value={form.salary} onChange={e=>set('salary',e.target.value)} placeholder="e.g. 120000" /></div>
            <div className="form-group"><label>Reports To</label>
              <select value={form.reporting_to} onChange={e=>set('reporting_to',e.target.value)}>
                <option value="">None</option>
                {employees.map(e=><option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
              </select>
            </div>
            <div className="form-group full"><label>Notes</label><textarea value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="e.g. Promoted due to excellent performance in Q2 2025" /></div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving||!form.designation_id||!form.start_date}>{saving?'Saving…':'Confirm Promotion'}</button>
        </div>
      </div>
    </div>
  );
}

// ---- Education Modal ----
function EduModal({ empId, initial, onClose, onDone }) {
  const [form, setForm] = useState(initial || { employee_id: empId, institution:'', degree:'', field_of_study:'', start_year:'', end_year:'', grade:'', description:'' });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const submit = async () => {
    try {
      if (initial?.id) await api.put(`/education/${initial.id}`, form);
      else await api.post('/education', form);
      onDone();
    } catch (e) { alert(e.response?.data?.message || 'Error'); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header"><span className="modal-title">{initial?.id ? 'Edit' : 'Add'} Education</span><button className="btn btn-icon btn-outline" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group full"><label>Institution *</label><input value={form.institution} onChange={e=>set('institution',e.target.value)} placeholder="University of Colombo" /></div>
            <div className="form-group"><label>Degree / Qualification *</label><input value={form.degree} onChange={e=>set('degree',e.target.value)} placeholder="BSc (Hons)" /></div>
            <div className="form-group"><label>Field of Study</label><input value={form.field_of_study} onChange={e=>set('field_of_study',e.target.value)} placeholder="Computer Science" /></div>
            <div className="form-group"><label>Start Year</label><input type="number" value={form.start_year} onChange={e=>set('start_year',e.target.value)} placeholder="2018" /></div>
            <div className="form-group"><label>End Year</label><input type="number" value={form.end_year} onChange={e=>set('end_year',e.target.value)} placeholder="2022" /></div>
            <div className="form-group"><label>Grade / GPA</label><input value={form.grade} onChange={e=>set('grade',e.target.value)} placeholder="Second Upper / 3.5 GPA" /></div>
            <div className="form-group full"><label>Notes</label><textarea value={form.description} onChange={e=>set('description',e.target.value)} /></div>
          </div>
        </div>
        <div className="modal-footer"><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit}>Save</button></div>
      </div>
    </div>
  );
}

// ---- Internship Modal ----
function InternModal({ empId, initial, employees, onClose, onDone }) {
  const [form, setForm] = useState(initial || { employee_id: empId, university:'', supervisor_id:'', start_date:'', end_date:'', stipend:'', project_title:'', description:'', completion_status:'Ongoing' });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const submit = async () => {
    try {
      if (initial?.id) await api.put(`/internships/${initial.id}`, form);
      else await api.post('/internships', form);
      onDone();
    } catch (e) { alert(e.response?.data?.message || 'Error'); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header"><span className="modal-title">{initial?.id ? 'Edit' : 'Add'} Internship</span><button className="btn btn-icon btn-outline" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group full"><label>University / Institute</label><input value={form.university} onChange={e=>set('university',e.target.value)} placeholder="SLIIT, University of Moratuwa…" /></div>
            <div className="form-group"><label>Project Title</label><input value={form.project_title} onChange={e=>set('project_title',e.target.value)} /></div>
            <div className="form-group"><label>Supervisor</label>
              <select value={form.supervisor_id} onChange={e=>set('supervisor_id',e.target.value)}>
                <option value="">None</option>
                {employees.map(e=><option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Start Date</label><input type="date" value={form.start_date} onChange={e=>set('start_date',e.target.value)} /></div>
            <div className="form-group"><label>End Date</label><input type="date" value={form.end_date} onChange={e=>set('end_date',e.target.value)} /></div>
            <div className="form-group"><label>Monthly Stipend (LKR)</label><input type="number" value={form.stipend} onChange={e=>set('stipend',e.target.value)} /></div>
            <div className="form-group"><label>Status</label>
              <select value={form.completion_status} onChange={e=>set('completion_status',e.target.value)}>
                <option>Ongoing</option><option>Completed</option><option>Terminated</option>
              </select>
            </div>
            <div className="form-group full"><label>Description</label><textarea value={form.description} onChange={e=>set('description',e.target.value)} /></div>
          </div>
        </div>
        <div className="modal-footer"><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit}>Save</button></div>
      </div>
    </div>
  );
}

// ---- Letter Modal ----
function LetterModal({ emp, onClose }) {
  const history = emp.history || [];
  const internships = emp.internships || [];

  // Build period options from employment history
  const historyOptions = history.map((h) => ({
    id: `hist_${h.id}`,
    label: `${h.designation_title} (${h.employment_type}) — ${new Date(h.start_date).toLocaleDateString('en-GB')} → ${h.end_date ? new Date(h.end_date).toLocaleDateString('en-GB') : 'Present'}`,
    start_date: h.start_date,
    end_date: h.end_date,
  }));

  // Build period options from internships
  const internshipOptions = internships.map((i) => ({
    id: `intern_${i.id}`,
    label: `Internship — ${i.university||''}  ${new Date(i.start_date).toLocaleDateString('en-GB')} → ${i.end_date ? new Date(i.end_date).toLocaleDateString('en-GB') : 'Present'}`,
    start_date: i.start_date,
    end_date: i.end_date,
    university: i.university,
  }));

  const allPeriods = [...internshipOptions, ...historyOptions];

  const [type, setType]             = useState('');
  const [letterStyle, setLetterStyle] = useState('internship');
  const [timeType, setTimeType]     = useState('Full Time');
  const [selectedPeriod, setSelectedPeriod] = useState(allPeriods[0]?.id || '');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]   = useState('');
  const [signatory, setSignatory]   = useState('Mr. H. M. R. U. K. Bandara');
  const [sigTitle, setSigTitle]     = useState('Chief Executive Officer (CEO)');
  const [responsibilities, setResponsibilities] = useState(['']);
  const [generating, setGenerating] = useState(false);

  const addResp    = () => setResponsibilities(r => [...r, '']);
  const removeResp = i  => setResponsibilities(r => r.filter((_,idx) => idx !== i));
  const updateResp = (i, v) => setResponsibilities(r => r.map((x,idx) => idx===i ? v : x));

  // Get dates from selected period or custom
  const getPeriod = () => {
    const p = allPeriods.find(p => String(p.id) === String(selectedPeriod));
    return {
      startDate: customStart || p?.start_date || '',
      endDate:   customEnd   || p?.end_date   || '',
      university: p?.university || '',
    };
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { startDate, endDate, university } = getPeriod();
      const endpoint = type === 'Appointment' ? '/letters/appointment' : '/letters/service';
      const payload = {
        employeeId:    emp.id,
        signatoryName: signatory,
        signatoryTitle: sigTitle,
        timeType,
        startDate,
        endDate,
        letterStyle,
        university: university || null,
        responsibilities: responsibilities.filter(r => r.trim()),
      };
      const res = await api.post(endpoint, payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `${letterStyle}_Letter_${emp.employee_id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Failed to generate letter.'); }
    setGenerating(false);
  };

  const LETTER_TYPES = [
    { k:'internship',  label:'Internship Letter',   desc:'Completion of internship program' },
    { k:'associate',   label:'Associate Letter',     desc:'Associate Software Engineer completion' },
    { k:'permanent',   label:'Employment Certificate', desc:'Certificate of employment / service' },
    { k:'appointment', label:'Appointment Letter',   desc:'Formal appointment & terms' },
  ];

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg" style={{maxWidth:680}}>
        <div className="modal-header">
          <span className="modal-title">Generate Letter</span>
          <button className="btn btn-icon btn-outline" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{maxHeight:'72vh',overflowY:'auto'}}>

          {/* Step 1 — Letter Type */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:12,fontWeight:700,color:'var(--slate-500)',marginBottom:10,textTransform:'uppercase',letterSpacing:'.5px'}}>1. Select Letter Type</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              {LETTER_TYPES.map(({k,label,desc}) => (
                <div key={k} onClick={() => { setType('Service'); setLetterStyle(k); }}
                  style={{padding:'12px 14px',borderRadius:10,cursor:'pointer',border:`2px solid ${letterStyle===k&&type==='Service'?'var(--indigo)':'var(--slate-200)'}`,background:letterStyle===k&&type==='Service'?'var(--indigo-pale)':'#fff',transition:'all .15s'}}>
                  <div style={{fontSize:13,fontWeight:600,color:letterStyle===k&&type==='Service'?'var(--indigo)':'var(--navy)'}}>{label}</div>
                  <div style={{fontSize:11,color:'var(--slate-500)',marginTop:3}}>{desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Step 2 — Part/Full time */}
          {(letterStyle === 'internship' || letterStyle === 'associate') && type && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:'var(--slate-500)',marginBottom:8,textTransform:'uppercase',letterSpacing:'.5px'}}>2. Employment Type</div>
              <div style={{display:'flex',gap:10}}>
                {['Full Time','Part Time'].map(t => (
                  <button key={t} onClick={() => setTimeType(t)}
                    style={{flex:1,padding:'9px',border:`2px solid ${timeType===t?'var(--indigo)':'var(--slate-200)'}`,borderRadius:8,background:timeType===t?'var(--indigo-pale)':'#fff',color:timeType===t?'var(--indigo)':'var(--slate-600)',fontSize:13,fontWeight:timeType===t?700:400,cursor:'pointer',transition:'all .15s'}}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3 — Period */}
          {type && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:'var(--slate-500)',marginBottom:8,textTransform:'uppercase',letterSpacing:'.5px'}}>3. Employment Period</div>
              {allPeriods.length > 0 && (
                <div style={{marginBottom:10}}>
                  <label style={{fontSize:12,fontWeight:600,color:'var(--slate-700)',display:'block',marginBottom:4}}>Select period</label>
                  <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
                    style={{width:'100%',padding:'9px 12px',border:'1px solid var(--slate-300)',borderRadius:7,fontSize:13}}>
                    <option value="">— Custom dates —</option>
                    {allPeriods.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                </div>
              )}
              {(!selectedPeriod || selectedPeriod==='') && (
                <div className="form-grid">
                  <div className="form-group"><label>Start Date</label><input type="date" value={customStart} onChange={e=>setCustomStart(e.target.value)} /></div>
                  <div className="form-group"><label>End Date</label><input type="date" value={customEnd} onChange={e=>setCustomEnd(e.target.value)} /></div>
                </div>
              )}
            </div>
          )}

          {/* Step 4 — Responsibilities */}
          {type && letterStyle !== 'appointment' && (
            <div style={{marginBottom:16}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                <div style={{fontSize:12,fontWeight:700,color:'var(--slate-500)',textTransform:'uppercase',letterSpacing:'.5px'}}>4. Duties & Responsibilities</div>
                <button className="btn btn-outline btn-sm" onClick={addResp}>+ Add</button>
              </div>
              {responsibilities.map((r,i) => (
                <div key={i} style={{display:'flex',gap:8,marginBottom:8,alignItems:'center'}}>
                  <span style={{width:24,height:24,background:'var(--indigo)',color:'#fff',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0}}>{i+1}</span>
                  <input value={r} onChange={e=>updateResp(i,e.target.value)} placeholder="e.g. Web development"
                    style={{flex:1,padding:'8px 12px',border:'1px solid var(--slate-300)',borderRadius:7,fontSize:13}} />
                  {responsibilities.length>1 && <button onClick={()=>removeResp(i)} style={{padding:'6px 10px',border:'1px solid var(--slate-200)',borderRadius:6,background:'#fff',color:'var(--red)',cursor:'pointer'}}>✕</button>}
                </div>
              ))}
            </div>
          )}

          {/* Step 5 — Signatory */}
          {type && (
            <div>
              <div style={{fontSize:12,fontWeight:700,color:'var(--slate-500)',marginBottom:8,textTransform:'uppercase',letterSpacing:'.5px'}}>5. Signatory</div>
              <div className="form-grid">
                <div className="form-group"><label>Name</label><input value={signatory} onChange={e=>setSignatory(e.target.value)} /></div>
                <div className="form-group"><label>Title</label><input value={sigTitle} onChange={e=>setSigTitle(e.target.value)} /></div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={generate} disabled={!type||generating}>
            <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" style={{width:14,height:14}}><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
            {generating ? 'Generating…' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Edit Personal Modal ----
function EditPersonalModal({ initial, onClose, onDone }) {
  const [form, setForm] = useState({ ...initial });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const submit = async () => {
    try { await api.put(`/employees/${initial.id}`, form); onDone(); }
    catch(e) { alert(e.response?.data?.message || 'Error'); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header"><span className="modal-title">Edit Personal Details</span><button className="btn btn-icon btn-outline" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group"><label>First Name</label><input value={form.first_name} onChange={e=>set('first_name',e.target.value)} /></div>
            <div className="form-group"><label>Last Name</label><input value={form.last_name} onChange={e=>set('last_name',e.target.value)} /></div>
            <div className="form-group"><label>Email</label><input value={form.email} onChange={e=>set('email',e.target.value)} /></div>
            <div className="form-group"><label>Phone</label><input value={form.phone||''} onChange={e=>set('phone',e.target.value)} /></div>
            <div className="form-group"><label>NIC</label><input value={form.nic||''} onChange={e=>set('nic',e.target.value)} /></div>
            <div className="form-group"><label>Date of Birth</label><input type="date" value={form.date_of_birth?.slice(0,10)||''} onChange={e=>set('date_of_birth',e.target.value)} /></div>
            <div className="form-group"><label>Gender</label><select value={form.gender||''} onChange={e=>set('gender',e.target.value)}><option value="">Select</option><option>Male</option><option>Female</option><option>Other</option></select></div>
            <div className="form-group"><label>Marital Status</label><select value={form.marital_status||''} onChange={e=>set('marital_status',e.target.value)}><option value="">Select</option><option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option></select></div>
            <div className="form-group"><label>Nationality</label><input value={form.nationality||''} onChange={e=>set('nationality',e.target.value)} /></div>
            <div className="form-group"><label>Status</label><select value={form.status} onChange={e=>set('status',e.target.value)}><option>Active</option><option>Inactive</option><option>Terminated</option></select></div>
            <div className="form-group full"><label>Address</label><textarea value={form.address||''} onChange={e=>set('address',e.target.value)} /></div>
            <div className="form-group"><label>Emergency Contact Name</label><input value={form.emergency_contact_name||''} onChange={e=>set('emergency_contact_name',e.target.value)} /></div>
            <div className="form-group"><label>Emergency Contact Phone</label><input value={form.emergency_contact_phone||''} onChange={e=>set('emergency_contact_phone',e.target.value)} /></div>
          </div>
        </div>
        <div className="modal-footer"><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit}>Save Changes</button></div>
      </div>
    </div>
  );
}

// ---- Main Profile Page ----
export default function EmployeeProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [designations, setDesignations] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [modal, setModal] = useState(null);
  const [letters, setLetters] = useState([]);

  const load = () => {
    api.get(`/employees/${id}`).then(r => setData(r.data.data));
    api.get(`/employees/${id}/letters`).then(r => setLetters(r.data.data));
  };

  useEffect(() => {
    load();
    api.get('/designations').then(r => setDesignations(r.data.data));
    api.get('/employees').then(r => setAllEmployees(r.data.data.filter(e => String(e.id) !== id)));
  }, [id]);

  if (!data) return <div style={{padding:40,textAlign:'center',color:'var(--slate-400)'}}>Loading profile…</div>;

  const current = data.history?.find(h => h.is_current);
  const initials = `${data.first_name?.[0]||''}${data.last_name?.[0]||''}`.toUpperCase();

  return (
    <div>
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar">{initials}</div>
        <div style={{flex:1}}>
          <div className="profile-name">{data.first_name} {data.last_name}</div>
          <div className="profile-role">{current?.designation_title || 'No designation'} · {current?.department || ''}</div>
          <div className="profile-meta">
            <span><svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" style={{width:12,height:12}}><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>{data.email}</span>
            {data.phone && <span><svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" style={{width:12,height:12}}><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>{data.phone}</span>}
            <span><span className="badge badge-slate" style={{fontSize:10}}>{data.employee_id}</span></span>
            <span className={`badge ${statusBadge(data.status)}`} style={{fontSize:10}}>{data.status}</span>
          </div>
        </div>
        <div style={{display:'flex',gap:10,flexShrink:0}}>
          <button className="btn btn-outline" style={{background:'rgba(255,255,255,.1)',border:'1px solid rgba(255,255,255,.2)',color:'#fff'}} onClick={()=>setModal('edit')}>Edit</button>
          <button className="btn btn-outline" style={{background:'rgba(255,255,255,.1)',border:'1px solid rgba(255,255,255,.2)',color:'#fff'}} onClick={()=>setModal('promote')}>Promote</button>
          <button className="btn btn-primary" onClick={()=>setModal('letter')}>
            <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
            Generate Letter
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[['overview','Overview'],['education','Education'],['internship','Internships'],['history','Career History'],['letters','Letters']].map(([k,l]) => (
          <div key={k} className={`tab ${tab===k?'active':''}`} onClick={()=>setTab(k)}>{l}</div>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
          <div className="card">
            <div className="card-header"><span className="card-title">Personal Information</span></div>
            <div className="card-body">
              {[['NIC',data.nic],['Date of Birth',fmt(data.date_of_birth)],['Gender',data.gender],['Marital Status',data.marital_status],['Nationality',data.nationality],['Address',data.address]].map(([k,v]) => (
                <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--slate-100)'}}>
                  <span style={{fontSize:12,color:'var(--slate-500)',fontWeight:600}}>{k}</span>
                  <span style={{fontSize:13,color:'var(--navy)',fontWeight:500,maxWidth:200,textAlign:'right'}}>{v||'—'}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:20}}>
            <div className="card">
              <div className="card-header"><span className="card-title">Current Position</span></div>
              <div className="card-body">
                {current ? (
                  <>
                    <div style={{fontSize:17,fontWeight:700,color:'var(--navy)'}}>{current.designation_title}</div>
                    <div style={{fontSize:13,color:'var(--slate-500)',marginTop:2}}>{current.department}</div>
                    <div style={{display:'flex',gap:8,marginTop:10,flexWrap:'wrap'}}>
                      <span className={`badge ${typeBadge(current.employment_type)}`}>{current.employment_type}</span>
                      <span className="badge badge-slate">Since {fmt(current.start_date)}</span>
                      {current.salary && <span className="badge badge-green">LKR {Number(current.salary).toLocaleString()}</span>}
                    </div>
                    {current.manager_name && <div style={{marginTop:8,fontSize:12,color:'var(--slate-500)'}}>Reports to: <strong>{current.manager_name}</strong></div>}
                  </>
                ) : <div style={{color:'var(--slate-400)',fontSize:13}}>No active position assigned.</div>}
              </div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">Emergency Contact</span></div>
              <div className="card-body">
                <div style={{fontSize:14,fontWeight:600,color:'var(--navy)'}}>{data.emergency_contact_name||'—'}</div>
                <div style={{fontSize:13,color:'var(--slate-500)',marginTop:4}}>{data.emergency_contact_phone||'—'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Education */}
      {tab === 'education' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Education</span>
            <button className="btn btn-primary btn-sm" onClick={()=>setModal('edu-add')}>
              <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 4v16m8-8H4"/></svg> Add
            </button>
          </div>
          <div className="card-body">
            {data.education?.length === 0 && <div className="empty-state"><p>No education records yet.</p></div>}
            {data.education?.map(edu => (
              <div key={edu.id} style={{border:'1px solid var(--slate-100)',borderRadius:8,padding:16,marginBottom:12}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div>
                    <div style={{fontWeight:700,color:'var(--navy)',fontSize:14}}>{edu.degree} {edu.field_of_study ? `in ${edu.field_of_study}` : ''}</div>
                    <div style={{fontSize:13,color:'var(--slate-500)',marginTop:2}}>{edu.institution}</div>
                    <div style={{display:'flex',gap:8,marginTop:8}}>
                      {edu.start_year && <span className="badge badge-slate">{edu.start_year} – {edu.end_year || 'Present'}</span>}
                      {edu.grade && <span className="badge badge-green">{edu.grade}</span>}
                    </div>
                    {edu.description && <div style={{fontSize:12,color:'var(--slate-500)',marginTop:6}}>{edu.description}</div>}
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>setModal({type:'edu-edit',data:edu})}>Edit</button>
                    <button className="btn btn-outline btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(window.confirm('Delete?')){await api.delete(`/education/${edu.id}`);load();}}}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Internships */}
      {tab === 'internship' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Internships</span>
            <button className="btn btn-primary btn-sm" onClick={()=>setModal('intern-add')}>
              <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 4v16m8-8H4"/></svg> Add
            </button>
          </div>
          <div className="card-body">
            {data.internships?.length === 0 && <div className="empty-state"><p>No internship records.</p></div>}
            {data.internships?.map(i => (
              <div key={i.id} style={{border:'1px solid var(--slate-100)',borderRadius:8,padding:16,marginBottom:12}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div>
                    <div style={{fontWeight:700,color:'var(--navy)',fontSize:14}}>{i.project_title || 'Internship'}</div>
                    <div style={{fontSize:13,color:'var(--slate-500)',marginTop:2}}>{i.university}</div>
                    <div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
                      <span className={`badge ${i.completion_status==='Completed'?'badge-green':i.completion_status==='Terminated'?'badge-red':'badge-amber'}`}>{i.completion_status}</span>
                      <span className="badge badge-slate">{fmt(i.start_date)} – {i.end_date ? fmt(i.end_date) : 'Present'}</span>
                      {i.stipend && <span className="badge badge-indigo">LKR {Number(i.stipend).toLocaleString()}/mo</span>}
                    </div>
                    {i.supervisor_name && <div style={{fontSize:12,color:'var(--slate-500)',marginTop:4}}>Supervisor: <strong>{i.supervisor_name}</strong></div>}
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>setModal({type:'intern-edit',data:i})}>Edit</button>
                    <button className="btn btn-outline btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(window.confirm('Delete?')){await api.delete(`/internships/${i.id}`);load();}}}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Career History */}
      {tab === 'history' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Career History</span>
            <span style={{fontSize:12,color:'var(--slate-500)'}}>{data.history?.length} position{data.history?.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="card-body">
            <div className="timeline">
              {data.history?.map(h => (
                <div key={h.id} className="timeline-item">
                  <div className={`timeline-dot ${h.is_current ? 'active' : ''}`} />
                  <div className="timeline-card">
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                      <div>
                        <div className="timeline-title">{h.designation_title}</div>
                        <div className="timeline-sub">{h.department}</div>
                        <div className="timeline-badge" style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                          {h.is_current && <span className="badge badge-green">Current</span>}
                          <span className={`badge ${typeBadge(h.employment_type)}`}>{h.employment_type}</span>
                          <span className="badge badge-slate">{fmt(h.start_date)} → {h.end_date ? fmt(h.end_date) : 'Present'}</span>
                          {h.salary && <span className="badge badge-indigo">LKR {Number(h.salary).toLocaleString()}</span>}
                        </div>
                        {h.manager_name && <div style={{fontSize:11,color:'var(--slate-500)',marginTop:4}}>Reported to: {h.manager_name}</div>}
                        {h.notes && <div style={{fontSize:12,color:'var(--slate-500)',marginTop:4,fontStyle:'italic'}}>"{h.notes}"</div>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {(!data.history || data.history.length === 0) && <div className="empty-state"><p>No history yet.</p></div>}
            </div>
          </div>
        </div>
      )}

      {/* Letters log */}
      {tab === 'letters' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Generated Letters</span>
            <button className="btn btn-primary btn-sm" onClick={()=>setModal('letter')}>Generate New</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Type</th><th>Reference No.</th><th>Generated By</th><th>Date</th></tr></thead>
              <tbody>
                {letters.map(l => (
                  <tr key={l.id}>
                    <td><span className={`badge ${l.letter_type==='Appointment'?'badge-indigo':'badge-green'}`}>{l.letter_type}</span></td>
                    <td><code style={{fontSize:12,fontFamily:'JetBrains Mono,monospace'}}>{l.reference_number}</code></td>
                    <td>{l.generated_by}</td>
                    <td style={{fontSize:12,color:'var(--slate-500)'}}>{new Date(l.generated_at).toLocaleString('en-GB')}</td>
                  </tr>
                ))}
                {letters.length === 0 && <tr><td colSpan={4}><div className="empty-state"><p>No letters generated yet.</p></div></td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {modal === 'promote' && <PromoteModal empId={id} designations={designations} employees={allEmployees} onClose={()=>setModal(null)} onDone={()=>{setModal(null);load();}} />}
      {modal === 'edit' && <EditPersonalModal initial={data} onClose={()=>setModal(null)} onDone={()=>{setModal(null);load();}} />}
      {modal === 'letter' && <LetterModal emp={data} onClose={()=>{setModal(null);load();}} />}
      {modal === 'edu-add' && <EduModal empId={id} onClose={()=>setModal(null)} onDone={()=>{setModal(null);load();}} />}
      {modal?.type === 'edu-edit' && <EduModal empId={id} initial={modal.data} onClose={()=>setModal(null)} onDone={()=>{setModal(null);load();}} />}
      {modal === 'intern-add' && <InternModal empId={id} employees={allEmployees} onClose={()=>setModal(null)} onDone={()=>{setModal(null);load();}} />}
      {modal?.type === 'intern-edit' && <InternModal empId={id} initial={modal.data} employees={allEmployees} onClose={()=>setModal(null)} onDone={()=>{setModal(null);load();}} />}
    </div>
  );
}
